<template>
  <div class="d-flex flex-column gap-2">
    <div v-if="modelValue.previousPeriods.length">
      <title-node>
        <span class="font-weight-medium"> Períodos de uso passados </span>
      </title-node>
      <ul class="font-size-14">
        <li v-for="(previousPeriod, i) in modelValue.previousPeriods" :key="i">
          {{ displayPeriod(previousPeriod) }}
        </li>
      </ul>
    </div>
    <v-form class="d-flex flex-column gap-2" v-model="validForm">
      <title-node>
        <span class="font-weight-medium"> Período atual </span>
      </title-node>
      <title-node>
        <span class="font-size-14 font-weight-medium"> Posologia </span>
      </title-node>
      <div class="d-flex flex-row flex-wrap gap-4 mb-2">
        <simple-text-input-component
          v-model="modelValue.actualPeriod.dosage"
          label="Dosagem"
          :suffix="dosageUnit"
          class="max-w-250"
        />
        <v-select
          v-model="modelValue.actualPeriod.frequency"
          label="Período"
          dense
          filled
          hide-details
          :items="['por dia', 'por semana', 'a cada (n) semanas']"
          class="max-w-250"
        />
        <simple-text-input-component
          v-if="modelValue.actualPeriod.frequency === 'a cada (n) semanas'"
          v-model="modelValue.actualPeriod.numWeeks"
          label="Nº de semanas"
          type="number"
          class="max-w-250"
        />
      </div>
      <div class="d-flex flex-row flex-wrap gap-4">
        <date-input-component
          v-model="modelValue.actualPeriod.startDate"
          label="Data de início"
          class="max-w-250"
          :max="$moment().toISOString()"
          :rules="[$validation.required()]"
        />
        <date-input-component
          v-model="modelValue.actualPeriod.endDate"
          :disabled="!actualPeriodDateMoment.startDate.isValid()"
          label="Data de fim"
          hint="Deixar em branco se estiver em uso"
          class="max-w-250"
          :max="$moment().toISOString()"
          :min="actualPeriodDateMoment.startDate.toISOString()"
          :rules="[$validation.dateAfter(modelValue.actualPeriod.startDate)]"
        />
        <v-select
          v-if="actualPeriodDateMoment.endDate.isValid()"
          v-model="modelValue.actualPeriod.stopReasons"
          label="Motivo"
          :items="apiStopReasons"
          item-value="id"
          item-text="name"
          return-object
          multiple
          filled
          dense
          chips
          small-chips
          deletable-chips
          :rules="[$validation.required()]"
          class="max-w-516"
        />
        <simple-text-input-component
          v-if="modelValue.actualPeriod.stopReasons.some((reason) => reason.name == 'Outros')"
          v-model="modelValue.actualPeriod.otherStopReason"
          label="Outro motivo"
          :rules="[$validation.required()]"
          class="max-w-516"
        />
      </div>
      <div class="d-flex flex-column gap-2">
        <div class="d-flex flex-row align-center gap-2">
          <title-node>
            <span class="font-weight-medium font-size-14"> Efeito adverso </span>
          </title-node>
          <v-tooltip bottom color="#333333">
            <template #activator="{ attrs, on }">
              <v-btn icon small color="primary" @click="addAdverseEffect" v-bind="attrs" v-on="on">
                <v-icon>mdi-plus-circle-outline</v-icon>
              </v-btn>
            </template>
            <span> Adcionar efeito adverso </span>
          </v-tooltip>
        </div>
        <div class="d-flex flex-row gap-4" v-for="(adverseEffect, i) in adverseEffects" :key="i">
          <simple-text-input-component
            v-model="adverseEffect.effect"
            label="Descreva o efeito adverso"
            hint="Reporte o efeito adverso à farmacêutica"
            class="max-w-782"
          />
          <date-input-component
            v-model="adverseEffect.date"
            label="Data do efeito"
            class="max-w-250"
            :min="actualPeriodDateMoment.startDate.toISOString()"
            :max="
              actualPeriodDateMoment.endDate.isValid()
                ? actualPeriodDateMoment.endDate.toISOString()
                : $moment().toISOString()
            "
          />
          <div class="d-flex align-center" style="height: 48px">
            <v-btn icon color="gray" :disabled="i == 0" @click="removeAdverseEffect(i)">
              <v-icon>mdi-delete-outline</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
      <v-btn small text color="primary" class="align-self-start mt-4" @click="registerPeriod" :disabled="!validForm">
        Registrar como uso passado
      </v-btn>
    </v-form>
  </div>
</template>

<script>
import { cloneDeep, isEmpty } from 'lodash'

import { PropOptions, WatchOptionsWithHandler } from 'vue'
import { Method, Computed } from '@/_typedefs'
import { Moment } from 'moment'
import TitleNode from '@/components/NewAttendanceForm/FormNodes/Title/TitleNode.vue'
import SimpleTextInputComponent from '@/components/NewAttendanceForm/Questions/Basic/TextInput/SimpleTextInput.component.vue'
import DateInputComponent from '@/components/NewAttendanceForm/Questions/Basic/TextInput/DateInput.component.vue'

/**
 * @typedef {Object} StopReason
 * @property {number} id
 * @property {string} name
 */

/**
 * @typedef {Object} Period
 * @property {string} dosage
 * @property {string} frequency
 * @property {string?} numWeeks
 * @property {string} startDate
 * @property {string} endDate
 * @property {StopReason[]} stopReasons
 * @property {string?} otherStopReason
 */

/**
 * @typedef {Object} FullPeriod
 * @property {Period} actualPeriod
 * @property {Period[]} previousPeriods
 */

/** @typedef {FullPeriod} ModelValue */

export default {
  name: 'PreviousTreatmentPeriodForm',
  components: { TitleNode, SimpleTextInputComponent, DateInputComponent },
  props: {
    /** @type {PropOptions<ModelValue>} */
    value: {
      type: Object,
      required: true,
    },
    dosageUnit: {
      type: String,
      default: 'mg',
    },
  },
  data() {
    /** @type {Period} */
    const basePeriod = {
      dosage: '',
      frequency: '',
      numWeeks: null,
      startDate: '',
      endDate: '',
      stopReasons: [],
      otherStopReason: null,
    }

    /** @type {ModelValue} */
    const modelValue = isEmpty(this.value)
      ? {
          actualPeriod: basePeriod,
          previousPeriods: [],
        }
      : cloneDeep(this.value)

    return {
      basePeriod,
      adverseEffects: [{ effect: '', date: '' }],
      /** @type {StopReason[]} */
      apiStopReasons: [],
      validForm: false,
      modelValue,
    }
  },
  computed: {
    /** @type {Computed<{startDate: Moment, endDate: Moment}>} */
    actualPeriodDateMoment() {
      return {
        startDate: this.$moment(this.modelValue.actualPeriod.startDate, 'DD/MM/YYYY', true),
        endDate: this.$moment(this.modelValue.actualPeriod.endDate, 'DD/MM/YYYY', true),
      }
    },
  },
  methods: {
    registerPeriod() {
      const valid = this.modelValue.previousPeriods.every((previousPeriod) => {
        const previousPeriodDateMoment = {
          startDate: this.$moment(previousPeriod.startDate, 'DD/MM/YYYY', true),
          endDate: this.$moment(previousPeriod.endDate, 'DD/MM/YYYY', true),
        }

        if (!previousPeriodDateMoment.endDate.isValid()) {
          previousPeriodDateMoment.endDate = this.$moment()
        }

        const periodEndDateOrToday = this.actualPeriodDateMoment.endDate.isValid()
          ? this.actualPeriodDateMoment.endDate
          : this.$moment()

        if (
          previousPeriodDateMoment.startDate.isBetween(
            this.actualPeriodDateMoment.startDate,
            periodEndDateOrToday,
            'day',
            '[]',
          ) ||
          previousPeriodDateMoment.endDate.isBetween(
            this.actualPeriodDateMoment.startDate,
            periodEndDateOrToday,
            'day',
            '[]',
          )
        ) {
          return false
        }

        return true
      })

      if (!valid) {
        this.$root.warn('O período informado se sobrepõe a outro período já informado, Verifique!')
        return
      }

      this.modelValue.previousPeriods.push(this.modelValue.actualPeriod)
      this.clearPeriod()
    },
    /** @type {Method<[index: number]>} */
    removePeriod(index) {
      this.modelValue.previousPeriods.splice(index, 1)
    },
    clearPeriod() {
      this.modelValue.actualPeriod = this.basePeriod
    },
    /** @type {Method<[period: Period], string>} */
    displayPeriod(period) {
      const dosage = `${period.dosage}mg`

      const frequency =
        period.frequency === 'a cada (n) semanas'
          ? period.frequency.replace('(n)', period.numWeeks?.toString() ?? '?')
          : period.frequency

      const datePeriod = `, de ${period.startDate} até ${period.endDate.length ? period.endDate : 'atualmente'}`

      const stopReason = period.stopReasons.length
        ? `- Motivo${period.stopReasons.length > 1 ? 's' : ''} da parada: ${period.stopReasons
            .map(({ name }) => name)
            .concat(!!period.otherStopReason ? [period.otherStopReason] : [])
            .join(', ')}`
        : null

      const adverseEffects = this.adverseEffects.filter(({ effect }) => effect.length).length
        ? `. Com os efeitos adversos: ${this.adverseEffects
            .map(({ effect, date }) => `${effect}, na data ${date}`)
            .join('; ')}`
        : null

      return [dosage, frequency, datePeriod, stopReason, adverseEffects].filter((part) => !!part).join(' ')
    },
    addAdverseEffect() {
      this.adverseEffects.push({ effect: '', date: '' })
    },
    /** @type {Method<[index: number]>} */
    removeAdverseEffect(index) {
      this.adverseEffects.splice(index, 1)
    },
  },
  watch: {
    /** @type {WatchOptionsWithHandler<ModelValue>} */
    modelValue: {
      handler(val) {
        this.$emit('input', val)
      },
      deep: true,
      immediate: true,
    },
  },
  async created() {
    try {
      this.apiStopReasons = await this.$api.getMedicineStopReasons()
    } catch (e) {
      this.$root.error(e)
    }
  },
}
</script>

<style scoped>
.max-w-250 {
  max-width: 250px;
}
.max-w-516 {
  max-width: 516px;
}
.max-w-782 {
  max-width: 782px;
}

:deep(.v-label),
:deep(input),
:deep(.v-text-field__suffix) {
  font-size: 14px;
}
:deep(.v-label--active) {
  transform: translateY(-10px) scale(0.85) !important;
}
:deep(.v-input__slot) {
  min-height: 48px !important;
}

:deep(.v-btn) {
  text-transform: none;
  border-radius: 6px;
}
</style>
